/**
 * FastLane MOQ Validator - Simplified
 *
 * Validates Minimum Order Quantity (MOQ) by reading metafields directly.
 * No DOM dependencies - just pure metafield checking.
 *
 * @version 2.0.0
 */

(function () {
  "use strict";

  class MOQValidator {
    constructor() {
      this.productHandle = null;
      this.moqData = null;
      this.productForm = null;
      this.quantityInput = null;

      this.init();
    }

    init() {
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () =>
          this.setup(),
        );
      } else {
        this.setup();
      }
    }

    async setup() {
      // If variant quantity drawer exists, skip PDP MOQ validation entirely.
      // The drawer handles MOQ enforcement across all variant quantities,
      // so we don't need per-item enforcement on the PDP.
      const drawer = document.querySelector('variant-quantity-drawer');
      if (drawer) {
        console.log("[MOQ Validator] Variant quantity drawer detected, skipping PDP MOQ validation");
        return;
      }

      // Find product form
      this.productForm = document.querySelector(
        'form[action*="/cart/add"]',
      );
      if (!this.productForm) {
        console.log("[MOQ Validator] No product form found");
        return;
      }

      // Find quantity input
      this.quantityInput = this.productForm.querySelector(
        'input[name="quantity"]',
      );
      if (!this.quantityInput) {
        console.log("[MOQ Validator] No quantity input found");
        return;
      }

      // Get MOQ data from DOM (Liquid-rendered elements have metafield access)
      this.moqData = this.getMOQFromDOM();

      if (!this.moqData || !this.moqData.moq || this.moqData.moq <= 1) {
        console.log(
          "[MOQ Validator] No MOQ requirements - validator disabled (MOQ:",
          this.moqData?.moq,
          ")",
        );
        return;
      }

      console.log(
        "[MOQ Validator] ✅ Initialized with MOQ:",
        this.moqData.moq,
        "Type:",
        this.moqData.type,
      );

      // Set initial quantity to MOQ if needed
      this.setInitialQuantity();

      // Attach form submit handler with capture phase to run FIRST
      this.productForm.addEventListener(
        "submit",
        (e) => this.onFormSubmit(e),
        true, // Use capture phase to intercept before other handlers
      );

      console.log(
        "[MOQ Validator] Attached submit handler to form:",
        this.productForm,
      );

      // Validate on quantity change
      this.quantityInput.addEventListener("input", () =>
        this.validateQuantity(),
      );
      this.quantityInput.addEventListener("change", () =>
        this.validateQuantity(),
      );

      // Also intercept AJAX add-to-cart (Shopify Dawn theme pattern)
      this.interceptAjaxCart();
    }

    /**
     * Get MOQ data from DOM elements (rendered by Liquid with metafield access)
     * This is the primary method since Shopify metafields aren't exposed to JSON API by default
     */
    getMOQFromDOM() {
      console.log(
        "[MOQ Validator] 🔍 Searching for MOQ data in DOM...",
      );

      // Check for tiered pricing element first
      const tieredElement = document.querySelector(
        ".fl-moq-tiered-pricing",
      );
      if (tieredElement && tieredElement.dataset.moq) {
        const moq = parseInt(tieredElement.dataset.moq) || 1;
        const increment =
          parseInt(tieredElement.dataset.increment) || 1;
        let tiers = [];

        if (tieredElement.dataset.tiers) {
          try {
            tiers = JSON.parse(tieredElement.dataset.tiers);
          } catch (e) {
            console.warn("[MOQ Validator] Failed to parse tiers", e);
          }
        }

        console.log(
          "[MOQ Validator] ✅ Found tiered pricing - MOQ:",
          moq,
        );
        return {
          type: "tiered",
          moq: moq,
          increment: increment,
          tiers: tiers,
        };
      }

      // Check for simple MOQ element
      const moqElement = document.querySelector(".fl-product-moq");
      if (moqElement) {
        const text = moqElement.textContent;
        const match = text.match(/(\d+)/);
        if (match) {
          const moq = parseInt(match[1]);
          console.log(
            "[MOQ Validator] ✅ Found simple MOQ - MOQ:",
            moq,
          );
          return {
            type: "single",
            moq: moq,
            increment: 1,
            tiers: [],
          };
        }
      }

      // Check for data-moq-config element (fallback)
      const configElement = document.querySelector("[data-moq-config]");
      if (configElement && configElement.dataset.moqConfig) {
        try {
          const config = JSON.parse(configElement.dataset.moqConfig);
          const moq = parseInt(config.moq) || 1;
          const increment = parseInt(config.increment) || 1;
          const tiers = config.tiers || [];

          console.log(
            "[MOQ Validator] ✅ Found MOQ config element - MOQ:",
            moq,
          );
          return {
            type: tiers.length > 0 ? "tiered" : "single",
            moq: moq,
            increment: increment,
            tiers: tiers,
          };
        } catch (e) {
          console.warn("[MOQ Validator] Failed to parse MOQ config", e);
        }
      }

      console.log("[MOQ Validator] ⚠️ No MOQ elements found in DOM");
      return null;
    }

    /**
     * Get product handle from URL
     */
    getProductHandle() {
      const match = window.location.pathname.match(
        /\/products\/([^/?]+)/,
      );
      return match ? match[1] : null;
    }

    /**
     * Fetch MOQ data directly from product metafields (FALLBACK - rarely works)
     * Note: Metafields must be exposed to Storefront API to work
     */
    async fetchMOQFromMetafields() {
      try {
        const response = await fetch(
          `/products/${this.productHandle}.js`,
        );
        const product = await response.json();

        // Check for tiered MOQ first
        if (product.metafields?.custom?.moq_tiers) {
          const tiers = product.metafields.custom.moq_tiers;
          return {
            type: "tiered",
            moq: tiers.moq || 1,
            increment: tiers.increment || 1,
            tiers: tiers.tiers || [],
          };
        }

        // Check for simple MOQ
        if (product.metafields?.custom?.moq) {
          return {
            type: "single",
            moq: product.metafields.custom.moq,
            increment: 1,
            tiers: [],
          };
        }

        return null;
      } catch (error) {
        console.error(
          "[MOQ Validator] Failed to fetch product:",
          error,
        );
        return null;
      }
    }

    /**
     * Set initial quantity to MOQ
     */
    setInitialQuantity() {
      const currentValue = parseInt(this.quantityInput.value) || 1;
      if (currentValue < this.moqData.moq) {
        this.quantityInput.value = this.moqData.moq;
        this.quantityInput.setAttribute("min", this.moqData.moq);
      }
    }

    /**
     * Validate quantity
     */
    validateQuantity() {
      const quantity = parseInt(this.quantityInput.value) || 1;
      const moq = this.moqData.moq;
      const increment = this.moqData.increment;

      this.clearError();

      // Check MOQ
      if (quantity < moq) {
        this.showError(`Minimum order quantity is ${moq} pieces`);
        return false;
      }

      // Check increment
      if (increment > 1 && (quantity - moq) % increment !== 0) {
        this.showError(`Please order in multiples of ${increment}`);
        return false;
      }

      return true;
    }

    /**
     * Handle form submit
     */
    onFormSubmit(event) {
      const quantity = parseInt(this.quantityInput.value) || 1;

      console.log(
        `[MOQ Validator] 🔍 Form Submit - Quantity: ${quantity}, MOQ: ${this.moqData.moq}`,
      );

      if (!this.validateQuantity()) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        console.error(
          `[MOQ Validator] ❌ BLOCKED - Quantity ${quantity} does not meet MOQ ${this.moqData.moq}`,
        );
        this.quantityInput.focus();
        return false;
      }

      console.log(
        `[MOQ Validator] ✅ ALLOWED - Quantity ${quantity} meets MOQ ${this.moqData.moq}`,
      );
      return true;
    }

    /**
     * Intercept AJAX add-to-cart requests
     * Many Shopify themes use fetch() to add items without form submission
     */
    interceptAjaxCart() {
      const originalFetch = window.fetch;
      const validator = this;

      window.fetch = async function (...args) {
        const [url, options] = args;

        if (
          url &&
          typeof url === "string" &&
          url.includes("/cart/add")
        ) {
          // Skip MOQ validation for multi-item requests (from variant quantity drawer)
          // The drawer handles its own MOQ validation internally
          let isMultiItem = false;
          if (options && options.body) {
            try {
              const body = typeof options.body === 'string' ? JSON.parse(options.body) : options.body;
              if (body.items && Array.isArray(body.items)) {
                isMultiItem = true;
              }
            } catch (e) { /* not JSON, continue with normal validation */ }
          }

          if (!isMultiItem) {
            const quantity = parseInt(validator.quantityInput?.value) || 1;

            console.log(
              `[MOQ Validator] 🔍 AJAX Cart Add - Quantity: ${quantity}, MOQ: ${validator.moqData.moq}`,
            );

            if (quantity < validator.moqData.moq) {
              console.error(
                `[MOQ Validator] ❌ BLOCKED AJAX - Quantity ${quantity} does not meet MOQ ${validator.moqData.moq}`,
              );

              validator.showError(
                `Minimum order quantity is ${validator.moqData.moq} pieces`,
              );

              return Promise.reject(
                new Error(
                  `MOQ not met: ${quantity} < ${validator.moqData.moq}`,
                ),
              );
            }

            console.log(
              `[MOQ Validator] ✅ ALLOWED AJAX - Quantity ${quantity} meets MOQ ${validator.moqData.moq}`,
            );
          } else {
            console.log(
              `[MOQ Validator] ⏭️ Skipping validation for multi-item cart add (handled by variant quantity drawer)`,
            );
          }
        }

        return originalFetch.apply(window, args);
      };

      console.log(
        "[MOQ Validator] AJAX cart interception enabled (fetch override)",
      );
    }

    /**
     * Show error message
     */
    showError(message) {
      this.clearError();

      const errorDiv = document.createElement("div");
      errorDiv.className = "fl-moq-error";
      errorDiv.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 1C4.13 1 1 4.13 1 8s3.13 7 7 7 7-3.13 7-7-3.13-7-7-7zm1 10.5h-2v-2h2v2zm0-3h-2v-5h2v5z" fill="currentColor"/>
        </svg>
        <span>${message}</span>
      `;

      const container =
        this.quantityInput.closest(".quantity") ||
        this.quantityInput.parentElement;
      container.appendChild(errorDiv);
      this.quantityInput.classList.add("fl-moq-error-input");
    }

    /**
     * Clear error message
     */
    clearError() {
      const error = this.productForm.querySelector(".fl-moq-error");
      if (error) error.remove();
      this.quantityInput.classList.remove("fl-moq-error-input");
    }
  }

  // Initialize
  new MOQValidator();
})();

/**
 * CSS Styles
 */
if (!document.getElementById("fl-moq-validator-styles")) {
  const style = document.createElement("style");
  style.id = "fl-moq-validator-styles";
  style.textContent = `
    .fl-moq-error {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem;
      margin-top: 0.5rem;
      background: #fff4f4;
      border: 1px solid #ffcdd2;
      border-radius: 4px;
      color: #d32f2f;
      font-size: 0.875rem;
      animation: fl-moq-slide-in 0.3s ease;
    }
    
    .fl-moq-error svg {
      flex-shrink: 0;
      color: #d32f2f;
    }
    
    .fl-moq-error-input {
      border-color: #d32f2f !important;
      outline: 2px solid #ffcdd2 !important;
    }
    
    @keyframes fl-moq-slide-in {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @media (max-width: 767px) {
      .fl-moq-error {
        font-size: 0.8125rem;
        padding: 0.625rem;
      }
    }
  `;
  document.head.appendChild(style);
}
